import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { Scoring } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import { InputText, ValidatedText, InputSelect, DataSourceLookup, InputNullableInt, ValidatedInt } from '@/components';
import { useCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';
import { useValidator } from '@/composeables';

export class ScoringGuiDef extends GuiDef<Scoring> {
    constructor() {
        super('Scoring');

        const Common = storeToRefs(useCommonStore());
        const OrganizersSource = computed(() => {
            return Common.OrganizersAsSelectLine.value;
        });
        const SportsSource = computed(() => {
            return Common.SportsAsSelectLine.value;
        });

        const NameValidator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        const Name = new FieldDef<Scoring>();
        Name.Name = 'Name';
        Name.FieldName = 'Name';
        Name.DefinedFor = 'Both';
        Name.TranslationKey = 'scoring.name';
        Name.Editable = true;
        Name.Filterable = true;
        Name.Sortable = true;
        Name.CssClasses = '';
        Name.value = (scoring) => scoring.Name;
        Name.setValue = (scoring, value) => (scoring.Name = value);
        Name.DetailEditor = markRaw(InputText);
        Name.DetailEditorProps = {};
        Name.CellEditor = markRaw(InputText);
        Name.CellEditorProps = {};
        Name.CellRenderer = markRaw(ValidatedText);
        Name.CellRendererProps = {};
        Name.Validator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        Name.SortDirection = undefined;
        Name.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(NameValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(NameValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        Name.MinWidth = undefined;
        Name.MaxWidth = undefined;
        Name.Tooltip = null;
        this.Fields.push(Name);

        const OrganizerID = new FieldDef<Scoring>();
        OrganizerID.Name = 'OrganizerID';
        OrganizerID.FieldName = 'OrganizerID';
        OrganizerID.DefinedFor = 'Both';
        OrganizerID.TranslationKey = 'scoring.organizerid';
        OrganizerID.Editable = true;
        OrganizerID.Filterable = true;
        OrganizerID.Sortable = true;
        OrganizerID.CssClasses = '';
        OrganizerID.value = (scoring) => scoring.OrganizerID;
        OrganizerID.setValue = (scoring, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            scoring.OrganizerID = value;
        };
        OrganizerID.DetailEditor = markRaw(InputSelect);
        OrganizerID.DetailEditorProps = { datasource: { mapsource: OrganizersSource } };
        OrganizerID.CellEditor = markRaw(InputSelect);
        OrganizerID.CellEditorProps = { datasource: { mapsource: OrganizersSource } };
        // @ts-expect-error @ts-ignore
        OrganizerID.CellRenderer = markRaw(DataSourceLookup);
        OrganizerID.CellRendererProps = { datasource: { mapsource: OrganizersSource } };
        OrganizerID.ExportValueFormatter = (obj: Scoring) => {
            return OrganizersSource.value.get(obj.OrganizerID)?.text;
        };
        OrganizerID.HeaderFilterValueGetter = (row) =>
            (OrganizersSource.value.get(row.OrganizerID)?.text ?? row.OrganizerID ?? '').toString();
        OrganizerID.Validator = null;
        OrganizerID.SortDirection = undefined;
        OrganizerID.cellClassRules = undefined;
        OrganizerID.MinWidth = undefined;
        OrganizerID.MaxWidth = undefined;
        OrganizerID.Tooltip = null;
        this.Fields.push(OrganizerID);

        const SportID = new FieldDef<Scoring>();
        SportID.Name = 'SportID';
        SportID.FieldName = 'SportID';
        SportID.DefinedFor = 'Both';
        SportID.TranslationKey = 'scoring.sportid';
        SportID.Editable = true;
        SportID.Filterable = true;
        SportID.Sortable = true;
        SportID.CssClasses = '';
        SportID.value = (scoring) => scoring.SportID;
        SportID.setValue = (scoring, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            scoring.SportID = value;
        };
        SportID.DetailEditor = markRaw(InputSelect);
        SportID.DetailEditorProps = { datasource: { mapsource: SportsSource } };
        SportID.CellEditor = markRaw(InputSelect);
        SportID.CellEditorProps = { datasource: { mapsource: SportsSource } };
        // @ts-expect-error @ts-ignore
        SportID.CellRenderer = markRaw(DataSourceLookup);
        SportID.CellRendererProps = { datasource: { mapsource: SportsSource } };
        SportID.ExportValueFormatter = (obj: Scoring) => {
            return SportsSource.value.get(obj.SportID)?.text;
        };
        SportID.Validator = null;
        SportID.SortDirection = undefined;
        SportID.cellClassRules = undefined;
        SportID.MinWidth = undefined;
        SportID.MaxWidth = undefined;
        SportID.Tooltip = null;
        this.Fields.push(SportID);

        const DNSPoints = new FieldDef<Scoring>();
        DNSPoints.Name = 'DNSPoints';
        DNSPoints.FieldName = 'DNSPoints';
        DNSPoints.DefinedFor = 'Both';
        DNSPoints.TranslationKey = 'scoring.dnspoints';
        DNSPoints.Editable = true;
        DNSPoints.Filterable = false;
        DNSPoints.Sortable = true;
        DNSPoints.CssClasses = '';
        DNSPoints.value = (scoring) => scoring.DNSPoints;
        DNSPoints.setValue = (scoring, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            scoring.DNSPoints = value;
        };
        DNSPoints.DetailEditor = markRaw(InputNullableInt);
        DNSPoints.DetailEditorProps = {};
        DNSPoints.CellEditor = markRaw(InputNullableInt);
        DNSPoints.CellEditorProps = {};
        DNSPoints.CellRenderer = markRaw(ValidatedInt);
        DNSPoints.CellRendererProps = {};
        DNSPoints.Validator = null;
        DNSPoints.SortDirection = undefined;
        DNSPoints.cellClassRules = undefined;
        DNSPoints.MinWidth = 80;
        DNSPoints.MaxWidth = 80;
        DNSPoints.Tooltip = null;
        this.Fields.push(DNSPoints);

        const DNFPoints = new FieldDef<Scoring>();
        DNFPoints.Name = 'DNFPoints';
        DNFPoints.FieldName = 'DNFPoints';
        DNFPoints.DefinedFor = 'Both';
        DNFPoints.TranslationKey = 'scoring.dnfpoints';
        DNFPoints.Editable = true;
        DNFPoints.Filterable = false;
        DNFPoints.Sortable = true;
        DNFPoints.CssClasses = '';
        DNFPoints.value = (scoring) => scoring.DNFPoints;
        DNFPoints.setValue = (scoring, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            scoring.DNFPoints = value;
        };
        DNFPoints.DetailEditor = markRaw(InputNullableInt);
        DNFPoints.DetailEditorProps = {};
        DNFPoints.CellEditor = markRaw(InputNullableInt);
        DNFPoints.CellEditorProps = {};
        DNFPoints.CellRenderer = markRaw(ValidatedInt);
        DNFPoints.CellRendererProps = {};
        DNFPoints.Validator = null;
        DNFPoints.SortDirection = undefined;
        DNFPoints.cellClassRules = undefined;
        DNFPoints.MinWidth = 80;
        DNFPoints.MaxWidth = 80;
        DNFPoints.Tooltip = null;
        this.Fields.push(DNFPoints);

        const DSQPoints = new FieldDef<Scoring>();
        DSQPoints.Name = 'DSQPoints';
        DSQPoints.FieldName = 'DSQPoints';
        DSQPoints.DefinedFor = 'Both';
        DSQPoints.TranslationKey = 'scoring.dsqpoints';
        DSQPoints.Editable = true;
        DSQPoints.Filterable = false;
        DSQPoints.Sortable = true;
        DSQPoints.CssClasses = '';
        DSQPoints.value = (scoring) => scoring.DSQPoints;
        DSQPoints.setValue = (scoring, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            scoring.DSQPoints = value;
        };
        DSQPoints.DetailEditor = markRaw(InputNullableInt);
        DSQPoints.DetailEditorProps = {};
        DSQPoints.CellEditor = markRaw(InputNullableInt);
        DSQPoints.CellEditorProps = {};
        DSQPoints.CellRenderer = markRaw(ValidatedInt);
        DSQPoints.CellRendererProps = {};
        DSQPoints.Validator = null;
        DSQPoints.SortDirection = undefined;
        DSQPoints.cellClassRules = undefined;
        DSQPoints.MinWidth = 80;
        DSQPoints.MaxWidth = 80;
        DSQPoints.Tooltip = null;
        this.Fields.push(DSQPoints);

        const DNQPoints = new FieldDef<Scoring>();
        DNQPoints.Name = 'DNQPoints';
        DNQPoints.FieldName = 'DNQPoints';
        DNQPoints.DefinedFor = 'Both';
        DNQPoints.TranslationKey = 'scoring.dnqpoints';
        DNQPoints.Editable = true;
        DNQPoints.Filterable = false;
        DNQPoints.Sortable = true;
        DNQPoints.CssClasses = '';
        DNQPoints.value = (scoring) => scoring.DNQPoints;
        DNQPoints.setValue = (scoring, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            scoring.DNQPoints = value;
        };
        DNQPoints.DetailEditor = markRaw(InputNullableInt);
        DNQPoints.DetailEditorProps = {};
        DNQPoints.CellEditor = markRaw(InputNullableInt);
        DNQPoints.CellEditorProps = {};
        DNQPoints.CellRenderer = markRaw(ValidatedInt);
        DNQPoints.CellRendererProps = {};
        DNQPoints.Validator = null;
        DNQPoints.SortDirection = undefined;
        DNQPoints.cellClassRules = undefined;
        DNQPoints.MinWidth = 80;
        DNQPoints.MaxWidth = 80;
        DNQPoints.Tooltip = null;
        this.Fields.push(DNQPoints);

        const LAPPoints = new FieldDef<Scoring>();
        LAPPoints.Name = 'LAPPoints';
        LAPPoints.FieldName = 'LAPPoints';
        LAPPoints.DefinedFor = 'Both';
        LAPPoints.TranslationKey = 'scoring.lappoints';
        LAPPoints.Editable = true;
        LAPPoints.Filterable = false;
        LAPPoints.Sortable = true;
        LAPPoints.CssClasses = '';
        LAPPoints.value = (scoring) => scoring.LAPPoints;
        LAPPoints.setValue = (scoring, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            scoring.LAPPoints = value;
        };
        LAPPoints.DetailEditor = markRaw(InputNullableInt);
        LAPPoints.DetailEditorProps = {};
        LAPPoints.CellEditor = markRaw(InputNullableInt);
        LAPPoints.CellEditorProps = {};
        LAPPoints.CellRenderer = markRaw(ValidatedInt);
        LAPPoints.CellRendererProps = {};
        LAPPoints.Validator = null;
        LAPPoints.SortDirection = undefined;
        LAPPoints.cellClassRules = undefined;
        LAPPoints.MinWidth = 80;
        LAPPoints.MaxWidth = 80;
        LAPPoints.Tooltip = null;
        this.Fields.push(LAPPoints);

        const CompletedPoints = new FieldDef<Scoring>();
        CompletedPoints.Name = 'CompletedPoints';
        CompletedPoints.FieldName = 'CompletedPoints';
        CompletedPoints.DefinedFor = 'Both';
        CompletedPoints.TranslationKey = 'scoring.completedpoints';
        CompletedPoints.Editable = true;
        CompletedPoints.Filterable = false;
        CompletedPoints.Sortable = true;
        CompletedPoints.CssClasses = '';
        CompletedPoints.value = (scoring) => scoring.CompletedPoints;
        CompletedPoints.setValue = (scoring, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            scoring.CompletedPoints = value;
        };
        CompletedPoints.DetailEditor = markRaw(InputNullableInt);
        CompletedPoints.DetailEditorProps = {};
        CompletedPoints.CellEditor = markRaw(InputNullableInt);
        CompletedPoints.CellEditorProps = {};
        CompletedPoints.CellRenderer = markRaw(ValidatedInt);
        CompletedPoints.CellRendererProps = {};
        CompletedPoints.Validator = null;
        CompletedPoints.SortDirection = undefined;
        CompletedPoints.cellClassRules = undefined;
        CompletedPoints.MinWidth = 120;
        CompletedPoints.MaxWidth = 120;
        CompletedPoints.Tooltip = null;
        this.Fields.push(CompletedPoints);

        const NonRankedPoints = new FieldDef<Scoring>();
        NonRankedPoints.Name = 'NonRankedPoints';
        NonRankedPoints.FieldName = 'NonRankedPoints';
        NonRankedPoints.DefinedFor = 'Both';
        NonRankedPoints.TranslationKey = 'scoring.nonrankedpoints';
        NonRankedPoints.Editable = true;
        NonRankedPoints.Filterable = false;
        NonRankedPoints.Sortable = true;
        NonRankedPoints.CssClasses = '';
        NonRankedPoints.value = (scoring) => scoring.NonRankedPoints;
        NonRankedPoints.setValue = (scoring, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            scoring.NonRankedPoints = value;
        };
        NonRankedPoints.DetailEditor = markRaw(InputNullableInt);
        NonRankedPoints.DetailEditorProps = {};
        NonRankedPoints.CellEditor = markRaw(InputNullableInt);
        NonRankedPoints.CellEditorProps = {};
        NonRankedPoints.CellRenderer = markRaw(ValidatedInt);
        NonRankedPoints.CellRendererProps = {};
        NonRankedPoints.Validator = null;
        NonRankedPoints.SortDirection = undefined;
        NonRankedPoints.cellClassRules = undefined;
        NonRankedPoints.MinWidth = 120;
        NonRankedPoints.MaxWidth = 120;
        NonRankedPoints.Tooltip = null;
        this.Fields.push(NonRankedPoints);

        const ShootingPoints = new FieldDef<Scoring>();
        ShootingPoints.Name = 'ShootingPoints';
        ShootingPoints.FieldName = 'ShootingPoints';
        ShootingPoints.DefinedFor = 'Both';
        ShootingPoints.TranslationKey = 'scoring.shootingpoints';
        ShootingPoints.Editable = true;
        ShootingPoints.Filterable = false;
        ShootingPoints.Sortable = true;
        ShootingPoints.CssClasses = '';
        ShootingPoints.value = (scoring) => scoring.ShootingPoints;
        ShootingPoints.setValue = (scoring, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            scoring.ShootingPoints = value;
        };
        ShootingPoints.DetailEditor = markRaw(InputNullableInt);
        ShootingPoints.DetailEditorProps = {};
        ShootingPoints.CellEditor = markRaw(InputNullableInt);
        ShootingPoints.CellEditorProps = {};
        ShootingPoints.CellRenderer = markRaw(ValidatedInt);
        ShootingPoints.CellRendererProps = {};
        ShootingPoints.Validator = null;
        ShootingPoints.SortDirection = undefined;
        ShootingPoints.cellClassRules = undefined;
        ShootingPoints.MinWidth = 120;
        ShootingPoints.MaxWidth = 120;
        ShootingPoints.Tooltip = null;
        this.Fields.push(ShootingPoints);
    }

    public async LoadDependencies() {
        await useCommonStore().EnsureLoaded(['Organizers']);
        await useCommonStore().EnsureLoaded(['Federations']);
    }
}
